/* ============================================================
   NADI — PDP gallery (arrows + thumbs + swipe)

   The swipe/drag gesture itself needs NO JavaScript: the track is a
   plain horizontally-scrolling flex container with CSS scroll-snap.
   This file keeps arrows/thumbs/count in sync with the visible slide,
   lets a control click scroll to its slide, and drops a slide whose
   image 404s so a broken asset never stays in the gallery. Without
   this file the gallery is still swipeable and the first image still
   renders; only the pager highlighting stays inert.
   ============================================================ */

function nadiInitGalleries() {
  document.querySelectorAll('[data-nadi-gallery]').forEach((gallery) => {
    if (gallery.dataset.nadiGalleryInit) return;
    gallery.dataset.nadiGalleryInit = '1';

    const track = gallery.querySelector('[data-nadi-gallery-track]');
    const status = gallery.querySelector('[data-nadi-gallery-status]');
    const prevBtn = gallery.querySelector('[data-nadi-gallery-prev]');
    const nextBtn = gallery.querySelector('[data-nadi-gallery-next]');
    const thumbsRow = gallery.querySelector('[data-nadi-gallery-thumbs]');
    const allSlides = Array.from(gallery.querySelectorAll('[data-nadi-gallery-slide]'));
    const allThumbs = Array.from(gallery.querySelectorAll('[data-nadi-gallery-thumb]'));

    if (!track || allSlides.length === 0) return;

    let activeIndex = 0;

    const visibleSlides = () => allSlides.filter((slide) => !slide.hidden);

    const controlAt = (index, nodes) =>
      nodes.find((el) => Number(el.dataset.index) === index);

    /* Native loading=lazy skips images that sit off-screen in this
       overflow track, and programmatic scrollLeft often does not start
       the fetch. Promote the visible slide and its neighbours to eager
       before they need to paint (slide 4 was the usual blank). */
    const eagerizeAround = (index) => {
      [index - 1, index, index + 1].forEach((i) => {
        const slide = allSlides[i];
        if (!slide || slide.hidden) return;
        const img = slide.querySelector('img');
        if (img && img.loading === 'lazy') img.loading = 'eager';
      });
    };

    const setActive = (index) => {
      const slides = visibleSlides();
      if (!slides.length) return;

      const requested = allSlides[index];
      const slide = requested && !requested.hidden ? requested : slides[0];
      activeIndex = allSlides.indexOf(slide);
      eagerizeAround(activeIndex);

      allThumbs.forEach((thumb) => {
        const on = Number(thumb.dataset.index) === activeIndex;
        thumb.classList.toggle('is-active', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      if (status) {
        const pos = slides.indexOf(slide) + 1;
        const of = status.dataset.of || 'of';
        status.textContent = `${pos} ${of} ${slides.length}`;
      }

      const activeThumb = controlAt(activeIndex, allThumbs);
      if (activeThumb && thumbsRow) {
        const left =
          activeThumb.offsetLeft - (thumbsRow.clientWidth - activeThumb.offsetWidth) / 2;
        thumbsRow.scrollLeft = Math.max(0, left);
      }
    };

    const goTo = (index) => {
      const slides = visibleSlides();
      if (!slides.length) return;

      const len = allSlides.length;
      let wrapped = ((index % len) + len) % len;
      let target = allSlides[wrapped];
      if (!target || target.hidden) {
        const dir = index >= activeIndex ? 1 : -1;
        for (let n = 0; n < len; n += 1) {
          wrapped = (wrapped + dir + len) % len;
          if (!allSlides[wrapped].hidden) {
            target = allSlides[wrapped];
            break;
          }
        }
      }
      if (!target || target.hidden) return;
      eagerizeAround(allSlides.indexOf(target));
      track.scrollLeft = target.offsetLeft;
      setActive(allSlides.indexOf(target));
    };

    const hideBroken = (slide) => {
      if (slide.hidden) return;
      slide.hidden = true;
      const index = allSlides.indexOf(slide);
      const thumb = controlAt(index, allThumbs);
      if (thumb) thumb.hidden = true;

      const remaining = visibleSlides();
      if (remaining.length < 2) {
        if (prevBtn) prevBtn.hidden = true;
        if (nextBtn) nextBtn.hidden = true;
        if (status) status.hidden = true;
        if (thumbsRow) thumbsRow.hidden = true;
      }
      if (!remaining.length) return;
      if (index === activeIndex) goTo(allSlides.indexOf(remaining[0]));
      else setActive(activeIndex);
    };

    allSlides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      img.addEventListener('error', () => hideBroken(slide));
      if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) {
        hideBroken(slide);
      }
    });

    allThumbs.forEach((el) => {
      el.addEventListener('click', () => {
        goTo(parseInt(el.dataset.index, 10) || 0);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', () => goTo(activeIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(activeIndex + 1));

    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(activeIndex + 1);
      }
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              const index = allSlides.indexOf(entry.target);
              if (index > -1 && !entry.target.hidden) setActive(index);
            }
          });
        },
        { root: track, threshold: [0.6] }
      );
      allSlides.forEach((slide) => observer.observe(slide));
    }

    setActive(0);
  });
}

nadiInitGalleries();
/* Theme editor replaces section DOM without a page load; re-scan so the
   pager stays live while Jake edits. Fresh nodes lack the init flag. */
document.addEventListener('shopify:section:load', nadiInitGalleries);
